/**
 * Build-time feature flags driven by Vite env vars.
 *
 * For now this is a single env-flag-per-flag setup; per-tenant resolution
 * (consulting a flag service via tenantId) is a deliberate follow-up. The
 * shape lets us swap callers later without touching their imports.
 */

export type SplitPayLayout = "pie" | "list" | "receipt";

const LAYOUT_VALUES = new Set<SplitPayLayout>(["pie", "list", "receipt"]);

function envLayout(): SplitPayLayout {
  const raw = import.meta.env.VITE_SPLIT_PAY_LAYOUT as string | undefined;
  if (raw && LAYOUT_VALUES.has(raw as SplitPayLayout)) {
    return raw as SplitPayLayout;
  }
  return "pie";
}

export function splitPayLayout(): SplitPayLayout {
  return envLayout();
}

/**
 * Checkout pricing mode (build-time `VITE_PRICE_QUOTE_MODE`).
 *
 * - "shadow" (default when unset or unknown): checkout prices the order
 *   locally (the same math as the cart) and creates it WITHOUT a `quoteId`,
 *   so order-service stays in shadow mode and keeps logging quote drift. No
 *   price-quote requests are issued.
 * - "enforce": server-priced checkout — totals come from the price quote and
 *   the order is placed with its `quoteId` (order-service 409s on drift).
 *
 * ETA / promise-time quotes (`useQuoteEta`) are unaffected by this flag.
 */
export type PriceQuoteMode = "enforce" | "shadow";

export function priceQuoteMode(raw: string | undefined = import.meta.env.VITE_PRICE_QUOTE_MODE as string | undefined): PriceQuoteMode {
  return raw?.trim().toLowerCase() === "enforce" ? "enforce" : "shadow";
}
